import { usePortalDropdownPosition } from '@hooks/usePortalDropdownPosition'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MenuButton, MenuItem, MenuPanel } from './ActionMenu.styles'

export interface ActionMenuItem {
	label: string
	onSelect: () => void
	danger?: boolean
}

export interface ActionMenuProps {
	/** The trigger's accessible name, naming what the menu acts on. */
	label: string
	/** The trigger's glyph. */
	icon: React.ReactNode
	/** Draw the trigger in full black rather than the quiet grey. */
	strong?: boolean
	items: ActionMenuItem[]
}

/**
 * A glyph button that opens a short menu of actions: a portaled panel on the
 * popover layer. React events bubble through a portal to whatever the button
 * sits in, so the trigger and the panel both stop theirs: a card underneath
 * neither opens nor starts a drag.
 *
 * The panel sits at the end of the body, out of the tab order's way, so the
 * keyboard follows the menu-button pattern: opening moves focus to the first
 * item, the arrows (and Home / End) walk the items, and Escape, Tab or a pick
 * closes the menu and hands focus back to the trigger.
 */
const ActionMenu: React.FC<ActionMenuProps> = ({ label, icon, strong, items }) => {
	const [open, setOpen] = useState(false)
	const anchorRef = useRef<HTMLButtonElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)
	const position = usePortalDropdownPosition(anchorRef, open)
	const positioned = position !== null

	const menuItems = (): HTMLButtonElement[] =>
		Array.from(panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])

	const closeToTrigger = useCallback(() => {
		setOpen(false)
		anchorRef.current?.focus()
	}, [])

	// The panel only mounts once it has a position, so that is when there is a first item to focus.
	useEffect(() => {
		if (open && positioned) menuItems()[0]?.focus()
	}, [open, positioned])

	useEffect(() => {
		if (!open) return
		const onMouseDown = (event: MouseEvent) => {
			const target = event.target as Node
			if (!anchorRef.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false)
		}
		// Only hears keys pressed elsewhere: the trigger and the panel stop theirs, and handle Escape themselves.
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false)
		}
		document.addEventListener('mousedown', onMouseDown)
		document.addEventListener('keydown', onKey)
		return () => {
			document.removeEventListener('mousedown', onMouseDown)
			document.removeEventListener('keydown', onKey)
		}
	}, [open])

	const handlePanelKeyDown = (event: React.KeyboardEvent) => {
		// Whatever the key, the card underneath never hears it.
		event.stopPropagation()
		const items = menuItems()
		const current = items.indexOf(document.activeElement as HTMLButtonElement)
		const focusAt = (index: number) => {
			event.preventDefault()
			items[(index + items.length) % items.length]?.focus()
		}
		if (event.key === 'ArrowDown') focusAt(current + 1)
		else if (event.key === 'ArrowUp') focusAt(current - 1)
		else if (event.key === 'Home') focusAt(0)
		else if (event.key === 'End') focusAt(items.length - 1)
		else if (event.key === 'Escape') closeToTrigger()
		// Not prevented: the browser carries on to whatever follows the trigger.
		else if (event.key === 'Tab') closeToTrigger()
	}

	return (
		<>
			<MenuButton
				ref={anchorRef}
				type="button"
				aria-label={label}
				$strong={strong}
				aria-haspopup="menu"
				aria-expanded={open}
				onPointerDown={event => event.stopPropagation()}
				onKeyDown={event => {
					event.stopPropagation()
					if (event.key === 'Escape') setOpen(false)
				}}
				onClick={event => {
					event.stopPropagation()
					setOpen(v => !v)
				}}
			>
				{icon}
			</MenuButton>
			{open &&
				position &&
				createPortal(
					<MenuPanel
						ref={panelRef}
						role="menu"
						$top={position.top}
						$right={position.left + position.width}
						onClick={event => event.stopPropagation()}
						onPointerDown={event => event.stopPropagation()}
						onKeyDown={handlePanelKeyDown}
					>
						{items.map(item => (
							<MenuItem
								key={item.label}
								type="button"
								role="menuitem"
								tabIndex={-1}
								$danger={item.danger}
								onClick={() => {
									closeToTrigger()
									item.onSelect()
								}}
							>
								{item.label}
							</MenuItem>
						))}
					</MenuPanel>,
					document.body,
				)}
		</>
	)
}

export default ActionMenu
